import React, { useId } from "react";
import * as Icons from "lucide-react";
import { useFocusTrap } from "../hooks/useFocusTrap";

// Fenêtre partagée (création, modification, confirmation). Fond beige comme les cartes (décision du 28/09,
// lot 7 : « modales de création en beige »).
// beforeClose (facultatif, peut être async) : appelé AVANT l'animation de fermeture ; s'il renvoie false,
// la fenêtre reste ouverte (ex. « Vos réponses ne seront pas conservées »). Sans lui, une question posée
// après l'animation laissait, en cas de refus, une fenêtre invisible qui bloquait la page.
// z : couche d'empilement (la fenêtre de confirmation passe au-dessus des autres fenêtres).
export const Modal = ({ children, onClose, title, wide, beforeClose, z = "z-[60]" }) => {
  const [closing, setClosing] = React.useState(false);
  const panelRef = React.useRef(null);
  const titleId = React.useId();
  const close = React.useCallback(async () => {
    if (beforeClose && !(await beforeClose())) return;
    setClosing(true);
    setTimeout(() => onClose && onClose(), 160);
  }, [onClose, beforeClose]);
  useFocusTrap(panelRef, true, close);
  return (
    <div className={`fixed inset-0 bg-voile/40 backdrop-blur-[3px] flex items-center justify-center ${z} p-4 ${closing ? "animate-overlay-out" : "animate-overlay"}`} onClick={close}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId}
        className={`bg-sable rounded-2xl border border-marine/10 shadow-lg p-7 w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto ${closing ? "animate-modal-out" : "animate-modal"}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-5">
          <h3 id={titleId} className="font-display text-xl text-marine min-w-0 break-words">{title}</h3>
          <button onClick={close} aria-label="Fermer" title="Fermer" className="w-8 h-8 -mr-1 rounded-lg flex items-center justify-center text-marine hover:bg-sable-clair shrink-0"><Icons.X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

// L'étiquette visible nomme aussi le champ pour les lecteurs d'écran (contrôle d'accessibilité du 02/10) : elle est
// reliée au champ natif unique qu'elle accompagne (input, select, textarea) ; un contenu composé garde l'étiquette seule.
export const Field = ({ label, children }) => {
  const autoId = useId();
  const single = React.isValidElement(children) && typeof children.type === "string";
  const id = single ? (children.props.id || autoId) : undefined;
  return (
    <div><label htmlFor={id} className="block text-xs font-semibold uppercase tracking-wider text-marine/80 mb-1.5">{label}</label>{single ? React.cloneElement(children, { id }) : children}</div>
  );
};

// ariaLabel : nom lu par les lecteurs d'écran quand le menu n'a pas d'étiquette visible (filtres d'une liste).
export const Select = ({ label, ariaLabel, value, onChange, options, testid }) => (
  <Field label={label}>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} aria-label={ariaLabel} className="inp">
      {options.map((o) => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </Field>
);
